import { useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  MessageCircle,
  ChevronDown,
  ChevronUp,
  Send,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { useMessageNotifications } from "@/hooks/use-message-notifications";
import { apiRequest } from "@/lib/queryClient";
import { format } from "date-fns";
import type { Message } from "@shared/schema";
import { useAuth } from "@/hooks/use-auth";

interface JamMessagingProps {
  jamNightId: number;
  disabled?: boolean;
  forceOpen?: boolean;
}

const messageFormSchema = z.object({
  content: z
    .string()
    .min(1, "Message cannot be empty")
    .max(500, "Message too long"),
});

type MessageFormData = z.infer<typeof messageFormSchema>;

export default function JamMessaging({ jamNightId, disabled = false, forceOpen }: JamMessagingProps) {
  // All hooks at the top
  const { hasNewMessages, isMessagingOpen, setIsMessagingOpen, messagingPanelRef } = useMessageNotifications();
  const { user } = useAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const form = useForm<MessageFormData>({
    resolver: zodResolver(messageFormSchema),
    defaultValues: {
      content: "",
    },
  });

  const { data: messages = [], isLoading } = useQuery<Message[]>({
    queryKey: [`/api/messages?jamNightId=${jamNightId}`],
    refetchInterval: 5000, // Poll every 5 seconds for new messages
  });

  // Derived state after hooks
  const openState = forceOpen || isMessagingOpen;
  const onOpenChangeHandler = setIsMessagingOpen;

  // When forceOpen is true (in chat tab), ensure messaging is marked as open
  useEffect(() => {
    if (forceOpen) {
      setIsMessagingOpen(true);
    }
  }, [forceOpen, setIsMessagingOpen]);

  // Auto-scroll to bottom when new messages arrive (only scroll within the message container)
  useEffect(() => {
    if (openState && messagesEndRef.current) {
      const scrollContainer = messagesEndRef.current.closest(
        "[data-radix-scroll-area-viewport]",
      );
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [messages, openState]);

  const createMessageMutation = useMutation({
    mutationFn: async (data: MessageFormData) => {
      const messageData = {
        jamNightId,
        content: data.content,
        senderName: user?.username || "Anonymous",
      };
      return apiRequest("POST", "/api/messages", messageData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/messages?jamNightId=${jamNightId}`],
      });
      form.reset({
        content: "",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: MessageFormData) => {
    createMessageMutation.mutate(data);
  };

  const formatMessageTime = (date: string | Date) => {
    return format(new Date(date), "HH:mm");
  };

  return (
    <Card
      ref={messagingPanelRef}
      className="mb-4 bg-white/20 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 overflow-hidden relative group scroll-mt-16"
      style={{
        background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
      }}
    >
      {/* Specular highlight overlay */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
          pointerEvents: 'none',
        }}
      />

      {/* Glass refraction effect */}
      <div
        className="absolute top-0 left-0 w-full h-px"
        style={{
          background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.8) 50%, transparent 100%)',
        }}
      />

      <Collapsible open={openState} onOpenChange={onOpenChangeHandler}>
        <CollapsibleTrigger asChild>
          <CardHeader
            className="cursor-pointer transition-colors relative z-10"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 100%)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3)',
            }}
          >
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <MessageCircle className="h-5 w-5 text-blue-600" />
                <span className="text-lg font-semibold text-slate-800">Chat</span>
                {hasNewMessages && (
                  <Badge
                    variant="destructive"
                    className="h-5 w-5 rounded-full p-0 flex items-center justify-center bg-red-500/80 backdrop-blur-sm border border-red-400/30"
                  >
                    <span className="text-xs">!</span>
                  </Badge>
                )}
                {messages.length > 0 && (
                  <Badge className="text-xs bg-blue-100/40 backdrop-blur-sm border-blue-200/50 shadow-sm text-blue-800">
                    {messages.length}
                  </Badge>
                )}
              </div>
              {openState ? (
                <ChevronUp className="h-4 w-4 text-slate-700" />
              ) : (
                <ChevronDown className="h-4 w-4 text-slate-700" />
              )}
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent
            className="pt-0 relative z-10"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
            }}
          >
            {/* Messages List */}
            <ScrollArea
              className="h-96 w-full rounded-xl p-3 mb-4 backdrop-blur-sm border border-white/20"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 100%)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2), 0 4px 16px rgba(0,0,0,0.05)',
              }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center h-full text-slate-500">
                  <Clock className="h-4 w-4 mr-2" />
                  Loading messages...
                </div>
              ) : messages.length === 0 ? (
                <div className="flex items-center justify-center h-full text-slate-500 text-center">
                  <div>
                    <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No messages yet</p>
                    <p className="text-xs">
                      Send the first message to get the conversation started
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {messages
                    .slice()
                    .reverse()
                    .map((message) => (
                      <div key={message.id} className="flex flex-col space-y-1">
                        <div className={`flex items-center space-x-2 text-xs text-slate-600 ${message.senderName === user?.username ? 'justify-end' : ''}`}>
                          <span className="font-medium">
                            {message.senderName}
                          </span>
                          <span>•</span>
                          <span>
                            {formatMessageTime(message.createdAt.toString())}
                          </span>
                        </div>
                        <div className={`flex ${message.senderName === user?.username ? 'justify-end' : 'justify-start'}`}>
                          <div
                            className={`rounded-xl px-3 py-2 text-sm backdrop-blur-sm border shadow-sm ${
                              message.senderName === user?.username
                                ? 'bg-blue-500/80 text-white border-blue-400/30'
                                : 'bg-white/60 text-slate-800 border-white/40'
                            }`}
                            style={{
                              boxShadow: message.senderName === user?.username
                                ? '0 4px 12px rgba(59, 130, 246, 0.15), inset 0 1px 0 rgba(255,255,255,0.2)'
                                : '0 4px 12px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.4)'
                            }}
                          >
                            {message.content}
                          </div>
                        </div>
                      </div>
                    ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>

            {/* Message Form */}
            <Form {...form}>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  form.handleSubmit(onSubmit)(e);
                }}
                className="space-y-3"
              >
                <div className="flex space-x-2">
                  <FormField
                    control={form.control}
                    name="content"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormControl>
                          <Textarea
                            placeholder={disabled ? "Messaging disabled for completed events" : "Type your message..."}
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            className="min-h-[60px] resize-none"
                            rows={2}
                            disabled={disabled}
                            {...field}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                e.stopPropagation();
                                form.handleSubmit(onSubmit)(e);
                              }
                            }}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <div className="flex items-stretch">
                    <Button
                      type="submit"
                      size="sm"
                      disabled={disabled || createMessageMutation.isPending || !user}
                      className="bg-blue-500/80 hover:bg-blue-600/80 backdrop-blur-sm border border-blue-400/30 shadow-lg text-white"
                      style={{
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2), inset 0 1px 0 rgba(255,255,255,0.2)',
                      }}
                    >
                      {createMessageMutation.isPending ? (
                        <Clock className="h-4 w-4" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </form>
            </Form>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}